<div class="">
  <div class="max-content-width spaced-sections">
    <div
      class="flex h-[700px] text-slate-900 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
    >
      <!-- Left Sidebar: Paper Picker -->
      <aside class="w-72 bg-slate-50 border-r border-slate-200 overflow-y-auto">
        <div class="p-2">
          <h2 class="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Project papers
          </h2>
          <app-paper-picker
            [papers]="papers()"
            [selectedId]="selectedPaperId()"
            (select)="onPaperSelected($event)"
          ></app-paper-picker>
        </div>
      </aside>

      <!-- Main area: toolbar + PDF -->
      <div class="flex-1 flex flex-col bg-white">
        <!-- Toolbar -->
        <div class="flex items-center gap-2 p-2 bg-slate-50 border-b border-slate-200 text-sm">
          @if (materializing()) {
            <span class="text-xs text-slate-500" data-testid="materializing-indicator"
              >Fetching open-access PDF...</span
            >
          } @else if (questionsLoading()) {
            <span class="text-xs text-slate-500" data-testid="questions-loading"
              >Loading questions…</span
            >
          } @else if (selectedPaperId() && !hasQuestions()) {
            <span class="text-xs text-slate-500" data-testid="no-questions-note">
              No extraction questions yet.
              <button
                type="button"
                class="text-blue-600 underline cursor-pointer"
                (click)="ctx.openExtractionQuestions()"
              >
                Define them
              </button>
              to annotate evidence.
            </span>
          }
          <button
            class="px-2 py-1 border rounded disabled:cursor-not-allowed disabled:opacity-50"
            [class.bg-emerald-500]="drawMode() === 'box'"
            [class.text-white]="drawMode() === 'box'"
            [class.border-emerald-600]="drawMode() === 'box'"
            [class.border-slate-300]="drawMode() !== 'box'"
            [class.hover:bg-slate-100]="drawMode() !== 'box'"
            [disabled]="!hasQuestions() || questionsLoading() || isReadOnly()"
            [title]="
              isReadOnly()
                ? 'Annotations are read-only because this project is complete.'
                : hasQuestions()
                  ? ''
                  : 'Define extraction questions for this project before annotating evidence.'
            "
            data-testid="draw-box-btn"
            (click)="toggleBoxMode()"
          >
            Box {{ drawMode() === 'box' ? 'ON' : 'OFF' }}
          </button>
          <button
            class="px-2 py-1 border rounded disabled:cursor-not-allowed disabled:opacity-50"
            [class.bg-emerald-500]="drawMode() === 'text'"
            [class.text-white]="drawMode() === 'text'"
            [class.border-emerald-600]="drawMode() === 'text'"
            [class.border-slate-300]="drawMode() !== 'text'"
            [class.hover:bg-slate-100]="drawMode() !== 'text'"
            [disabled]="!hasQuestions() || questionsLoading() || isReadOnly()"
            [title]="
              isReadOnly()
                ? 'Annotations are read-only because this project is complete.'
                : hasQuestions()
                  ? ''
                  : 'Define extraction questions for this project before annotating evidence.'
            "
            data-testid="draw-text-btn"
            (click)="toggleTextMode()"
          >
            Text {{ drawMode() === 'text' ? 'ON' : 'OFF' }}
          </button>
          <!-- C4: Unsaved changes badge (active document) -->
          @if (store.hasUnsubmittedChanges() && !submitting()) {
            <span
              class="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium border border-amber-300"
              data-testid="unsaved-badge"
            >
              Unsaved changes
            </span>
          }
          <!-- C2: Save button -->
          <button
            class="px-3 py-1 rounded border font-medium"
            [class.bg-blue-600]="store.hasUnsubmittedChanges() && !submitting() && !isReadOnly()"
            [class.text-white]="store.hasUnsubmittedChanges() && !submitting() && !isReadOnly()"
            [class.border-blue-700]="
              store.hasUnsubmittedChanges() && !submitting() && !isReadOnly()
            "
            [class.bg-slate-200]="!store.hasUnsubmittedChanges() || submitting() || isReadOnly()"
            [class.text-slate-500]="!store.hasUnsubmittedChanges() || submitting() || isReadOnly()"
            [class.border-slate-300]="
              !store.hasUnsubmittedChanges() || submitting() || isReadOnly()
            "
            [disabled]="!store.hasUnsubmittedChanges() || submitting() || isReadOnly()"
            data-testid="save-btn"
            (click)="onSubmit()"
          >
            {{ submitting() ? 'Saving...' : 'Save' }}
          </button>
          <button
            class="px-3 py-1 rounded border font-medium"
            [class.bg-amber-50]="store.hasUnsubmittedChanges() && !submitting()"
            [class.text-amber-700]="store.hasUnsubmittedChanges() && !submitting()"
            [class.border-amber-300]="store.hasUnsubmittedChanges() && !submitting()"
            [class.hover:bg-amber-100]="store.hasUnsubmittedChanges() && !submitting()"
            [class.bg-slate-200]="!store.hasUnsubmittedChanges() || submitting()"
            [class.text-slate-500]="!store.hasUnsubmittedChanges() || submitting()"
            [class.border-slate-300]="!store.hasUnsubmittedChanges() || submitting()"
            [disabled]="!store.hasUnsubmittedChanges() || submitting() || materializing()"
            (click)="onDiscard()"
          >
            Discard
          </button>
        </div>
        <!-- C9: Read-only note when project is COMPLETE -->
        @if (isReadOnly()) {
          <div
            class="px-3 py-2 bg-slate-100 border-b border-slate-200 text-slate-600 text-xs"
            data-testid="read-only-note"
          >
            This project is complete. Annotations are read-only and cannot be saved.
          </div>
        }
        @if (lastError()) {
          <div
            class="px-2 py-1 bg-red-50 border-b border-red-200 text-red-700 text-xs flex items-center gap-2"
          >
            <span class="flex-1">{{ lastError() }}</span>
            <button
              class="px-2 py-0.5 border border-red-300 rounded hover:bg-red-100"
              (click)="lastError.set(null)"
            >
              Dismiss
            </button>
          </div>
        }
        @if (questionsError()) {
          <div
            class="px-2 py-1 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs flex-1 flex items-center gap-2"
            data-testid="questions-error"
          >
            <span class="flex-1">{{ questionsError() }}</span>
            <button
              class="px-2 py-0.5 border border-amber-300 rounded hover:bg-amber-100"
              (click)="questionsError.set(null)"
            >
              Dismiss
            </button>
          </div>
        }

        <div class="flex-1 overflow-hidden relative bg-slate-100">
          @if (materializing()) {
            <div
              class="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75"
              data-testid="materializing-overlay"
            >
              <span class="text-sm text-slate-600 font-semibold"
                >Fetching open-access PDF, please wait...</span
              >
            </div>
          }
          <app-cad-pdf-viewer
            (pageRendered)="onPageRendered($event)"
            (loadFailed)="onViewerLoadFailed()"
          />
          @for (p of pages(); track p) {
            @if (geometryFor(p); as geom) {
              @if (pageElementFor(p); as pageEl) {
                <ng-container
                  appClaimAnnotatorOverlay
                  [overlayPageNumber]="p"
                  [overlayDocumentId]="activeDocumentId()"
                  [overlayGeometry]="geom"
                  [overlayPageElement]="pageEl"
                  [overlayDrawMode]="drawMode()"
                  (overlayDrawCompleted)="onOverlayDraw($event)"
                  (overlayTextPointPicked)="onOverlayTextPoint($event)"
                  (overlayEditNoteRequested)="onEditNoteRequested($event)"
                  (overlayDeleteRequested)="onDeleteAnnotation($event)"
                  (overlayEditOcrRequested)="onEditOcrRequested($event)"
                ></ng-container>
              }
            }
          }
        </div>
      </div>

      <!-- Right Sidebar: Claims -->
      <aside class="w-80 bg-slate-50 border-l border-slate-200">
        <app-cad-claims-sidebar />
      </aside>
    </div>
  </div>

  @if (workflow.pendingTextPoint(); as t) {
    <div
      [style.position]="'fixed'"
      [style.left.px]="t.clientX"
      [style.top.px]="t.clientY"
      style="z-index: 50"
    >
      <app-text-input-overlay
        (confirmed)="onTextInputConfirmed($event)"
        (cancelled)="onTextInputCancelled()"
      />
    </div>
  }
  @if (workflow.pendingDraft()) {
    <div style="position: fixed; left: 40%; top: 30%; z-index: 50">
      <app-claim-picker-popover
        [claims]="currentClaims()"
        (picked)="onClaimPicked($event)"
        (cancelled)="onClaimPickerCancelled()"
      />
    </div>
  }
  @if (workflow.editingNoteForId()) {
    <div style="position: fixed; left: 40%; top: 30%; z-index: 50">
      <app-note-editor
        [initial]="workflow.noteInitial()"
        (saved)="onNoteSaved($event)"
        (cancelled)="onNoteEditorCancelled()"
      />
    </div>
  }
  @if (workflow.editingOcrForId()) {
    <div style="position: fixed; left: 35%; top: 25%; z-index: 50">
      <app-ocr-editor
        [initial]="workflow.ocrInitial()"
        (saved)="onOcrSaved($event)"
        (cancelled)="onOcrEditorCancelled()"
      />
    </div>
  }
</div>
